// client/src/components/pages/LexiStar/ReviewQueue/assuranceAtoms.jsx
//
// Presentational pieces shared by the M3-U1 assurance pages: confidence chip
// (LS-04 §14 bands), review-state and record-state chips, KPI card, progress
// bar, an accessible modal, a reason field and the realtime hook for the
// assurance rooms. Chrome follows the M2 control-tower pages (slate + primary,
// rounded-xl cards); state colour is reserved for state (LS-09 §1.1) and every
// chip pairs colour with a glyph and words.

import React, { useEffect, useId, useRef, useState } from 'react';
import { FaLock, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../../../context/AuthContext';
import { subscribeAssurance } from '../../../../services/lexistarAssurance';
import { BAND_META, confidenceBand, REVIEW_STATE_META, RECORD_STATE_META } from './assuranceShared';
import { inputClasses } from '../StatusBoard/atoms';

export const buttonClasses = {
  primary:
    'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed',
  secondary:
    'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed',
  quiet:
    'inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 rounded-md hover:bg-slate-100 disabled:opacity-50',
  danger:
    'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-red-700 bg-surface border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed',
};

export function ConfidenceChip({ confidence, showLabel = true }) {
  const band = confidenceBand(confidence);
  if (!band) return <span className="text-xs text-slate-400">—</span>;
  const meta = BAND_META[band];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums whitespace-nowrap ${meta.className}`}
      title={`Confidence ${confidence} — ${meta.label.toLowerCase()} (LS-04 §14: ≥ 85 high, 70–84 medium, < 70 review)`}
      data-band={band}
    >
      <span aria-hidden="true">{meta.glyph}</span>
      {confidence}
      {showLabel && <span className="font-medium">· {meta.label}</span>}
    </span>
  );
}

export function ReviewStateChip({ state }) {
  const meta = REVIEW_STATE_META[state] || REVIEW_STATE_META.open;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${meta.className}`}
    >
      <span aria-hidden="true">{meta.glyph}</span>
      {meta.label}
    </span>
  );
}

export function RecordStateChip({ state }) {
  const meta = RECORD_STATE_META[state] || RECORD_STATE_META.OPEN;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${meta.className}`}
      title={meta.help}
      data-record-state={state || 'OPEN'}
    >
      {state === 'SIGNED_OFF' ? (
        <FaLock size={9} aria-hidden="true" />
      ) : (
        <span aria-hidden="true">{meta.glyph}</span>
      )}
      {meta.label}
    </span>
  );
}

/** Headline number with a label (tabular numerals; LS-09 §8). */
export function KpiCard({ label, value, hint, shortHint, tone = 'default', children }) {
  const labelId = useId();
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className={`rounded-xl border px-3 sm:px-4 py-3 bg-surface ${tone === 'warn' ? 'border-amber-300' : 'border-slate-200'}`}
    >
      <p id={labelId} className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-slate-900 tabular-nums leading-tight">{value}</p>
      {hint && (
        <p className="mt-0.5 text-xs text-slate-500">
          {shortHint ? (
            <>
              <span className="sm:hidden">{shortHint}</span>
              <span className="hidden sm:inline">{hint}</span>
            </>
          ) : (
            hint
          )}
        </p>
      )}
      {children}
    </div>
  );
}

export function ProgressBar({ value, label }) {
  const pct = Math.max(0, Math.min(100, Math.round((Number(value) || 0) * 100)));
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div
        className="h-1.5 flex-1 rounded-full bg-slate-200 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={label || 'Progress'}
      >
        <div className="h-full bg-slate-700 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs tabular-nums text-slate-600 w-9 text-right">{pct}%</span>
    </div>
  );
}

/**
 * Accessible modal: labelled dialog, Esc closes, focus moves in on open and back
 * to the opener on close. Full-screen sheet on a phone, centred card from sm up.
 */
export function Modal({ open, title, onClose, children, footer, wide = false }) {
  const ref = useRef(null);
  const opener = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    opener.current = document.activeElement;
    const node = ref.current;
    const first = node?.querySelector(
      'input, textarea, button:not([aria-label="Close"]), [tabindex]:not([tabindex="-1"])'
    );
    (first || node)?.focus?.();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      opener.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`bg-surface w-full ${wide ? 'sm:max-w-3xl' : 'sm:max-w-lg'} max-h-[92vh] sm:mx-4 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col outline-none`}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 -m-2 text-slate-400 hover:text-slate-600 rounded-lg"
          >
            <FaTimes size={16} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">{children}</div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Labelled reason field (every assurance decision is audited with one). */
export function ReasonField({
  value,
  onChange,
  label = 'Reason',
  required = true,
  placeholder = 'Why? Recorded in the audit trail.',
  rows = 2,
  autoFocus,
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
        {label}
        {!required && <span className="font-normal text-slate-500"> (optional)</span>}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={1000}
        placeholder={placeholder}
        className={inputClasses}
        autoFocus={autoFocus}
      />
    </div>
  );
}

/** A labelled field wrapper for custom controls (pickers, ThemedSelect). */
export function Field({ label, htmlFor, hint, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function InlineError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      {message}
    </p>
  );
}

/**
 * Realtime binding for the assurance rooms (m3 §2.4). Returns the connection status
 * for ConnectionIndicator. The tenant comes from AuthContext; nothing joins until it
 * is known.
 */
export function useAssuranceEvents({ review = false, enabled = true, onReview, onRecompute }) {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenant_id ?? null;
  const [status, setStatus] = useState('connecting');
  const handlers = useRef({ onReview, onRecompute });
  handlers.current = { onReview, onRecompute };
  useEffect(() => {
    if (!enabled || tenantId === null) {
      setStatus('offline');
      return undefined;
    }
    setStatus('connecting');
    return subscribeAssurance({
      tenantId,
      review,
      onReview: (m) => handlers.current.onReview?.(m),
      onRecompute: (m) => handlers.current.onRecompute?.(m),
      onStatus: setStatus,
    });
  }, [tenantId, review, enabled]);
  return status;
}

/** matchMedia-driven "desktop split view" flag (jsdom has no matchMedia → wide). */
export function useIsWide(query = '(min-width: 1024px)') {
  const get = () =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : window.matchMedia(query).matches;
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [query]);
  return wide;
}

/** Save text as a file download (template CSVs, error reports). */
export function downloadText(filename, text, type = 'text/csv;charset=utf-8') {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
